<template>
  <div class="text-dcblue bg-dcgray pb-16 page-content">
    <div class="header-img">
      <h1 class="self-center text-white text-26875 font-bold">Privacy Policy</h1>
    </div>

    <div class="md:w-1/2 mx-8 md:mx-auto privacy-policy">
        <h4 class="text-150 font-semibold">Last updated June 08, 2020</h4>
        <br>
        <p>Thank you for choosing to be part of our community at DanDocs. We are committed to protecting your personal information and your right to privacy. If you have any questions or concerns about our policy, or our practices with regards to your personal information, please contact us at support@dandocs.com.</p>
        <br>
        <p>When you visit our website <a href="https://www.marineregulations.com">https://www.marineregulations.com</a>, and use our services, you trust us with your personal information. We take your privacy very seriously. In this privacy policy, we seek to explain to you in the clearest way possible what information we collect, how we use it and what rights you have in relation to it. We hope you take some time to read through it carefully, as it is important. If there are any terms in this privacy policy that you do not agree with, please discontinue use of our website and our services.</p>
        <br>
        <p>This privacy policy applies to all information collected through our website (such as <a href="https://www.marineregulations.com">https://www.marineregulations.com</a>), and/or any related services, sales, marketing or events)</p>
        <br>
        <h3 class="text-175 font-semibold">Table of Contents</h3>
        <br>
            <ol class="table-of-contents">
              <li v-on:click="handleClick('1')">
                  What information do we collect?
              </li>
              <li v-on:click="handleClick('2')">
                  How do we use your information?
              </li>
              <li v-on:click="handleClick('3')">
                  Will your information be shared with anyone?
              </li>
              <li v-on:click="handleClick('4')">
                  Do we use cookies and other tracking technologies?
              </li>
              <li v-on:click="handleClick('5')">
                  How long do we keep your information?
              </li>
              <li v-on:click="handleClick('6')">
                  How do we keep your information safe?
              </li>
              <li v-on:click="handleClick('7')">
                  Do we collect information from minors?
              </li>
              <li v-on:click="handleClick('8')">
                 What are your privacy rights?
              </li>
              <li v-on:click="handleClick('9')">
                  Data breach
              </li>
              <li v-on:click="handleClick('10')">
                  Controls for do-not-track features
              </li>
              <li v-on:click="handleClick('11')">
                  Do california residents have specific privacy rights?
              </li>
              <li v-on:click="handleClick('12')">
                  Do we make updates to this policy?
              </li>
              <li v-on:click="handleClick('13')">
                  How can you contact us about this policy?
              </li>
            </ol>
            <br>
          <h3 id="1" class="text-175 font-semibold">1. What information do we collect?</h3>
          <br>
          <h4 class="text-lg font-semibold">Personal information you disclose to us</h4>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We collect personal information that you provide to us.</p>
          <br>
          <p>We collect personal information that you voluntarily provide to us when registering with DanDocs, expressing an interest in obtaining information about us or our products and services, when participating in activities on the Services or otherwise contacting us.</p>
          <br>
          <p>The personal information that we collect depends on the context of your interactions with us and on our website, the choices you make and the products and features you use. The personal information we collect can include the following:</p>
          <br>
          <p>
              <strong>Publicly Available Personal Information. </strong>
              We collect first name, maiden name, last name, and nickname; email addresses; and other similar data.
          </p>
          <br>
          <p>All personal information that you provide to us must be true, complete and accurate, and you must notify us of any changes to such personal information.</p>
          <br>
          <h4 class="text-lg font-semibold">Information automatically collected</h4>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              Some information — such as IP address and/or browser and device characteristics — is collected automatically when you visit our Services.
          </p>
          <br>
          <p>We automatically collect certain information when you visit, use or navigate on our website. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our services and other technical information. This information is primarily needed to maintain the security and operation of our services, and for our internal analytics and reporting purposes.</p>
          <br>
          <p>Like many businesses, we also collect information through cookies and similar technologies.</p>
          <br>
          <p>
              <strong>Online Identifiers. </strong>
              We collect devices; cookie identifiers, or others such as the ones used for analytics and marketing; and other similar data.
          </p>
          <br>
          <h3 id="2" class="text-175 font-semibold">2. How do we use your information?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We process your information for purposes based on legitimate business interests, the fulfillment of our contract with you, compliance with our legal obligations, and/or your consent.
          </p>
          <br>
          <p>We use personal information collected via our Services for a variety of business purposes described below. We process your personal information for these purposes in reliance on our legitimate business interests, in order to enter into or perform a contract with you, with your consent, and/or for compliance with our legal obligations. We indicate the specific processing grounds we rely on next to each purpose listed below.</p>
          <br>
          <p>We use the information we collect or receive:</p>
          <br>
          <ul>
              <li>
                  <strong>To facilitate account creation and logon process. </strong>
                  If you choose to link your account with us to a third party account (such as your Google or Facebook account), we use the information you allowed us to collect from those third parties to facilitate account creation and logon process for the performance of the contract.
              </li>
              <br>
              <li>
                  <strong>Request Feedback. </strong>
                  We may use your information to request feedback and to contact you about your use of our services.
              </li>
              <br>
              <li>
                  <strong>For other Business Purposes. </strong>
                  We may use your information for other Business Purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our services, products, marketing and your experience. We may use and store this information in aggregated and anonymized form so that it is not associated with individual end users and does not include personal information. We will not use identifiable personal information without your consent.
              </li>
          </ul>
          <br>
          <h3 id="3" class="text-175 font-semibold">3. Will your information be shared with anyone?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations.
          </p>
          <br>
          <p>We may process or share data based on the following legal basis:</p>
          <br>
          <ul>
              <li>
                  <strong>Consent: </strong>
                  We may process your data if you have given us specific consent to use your personal information in a specific purpose.
              </li>
              <br>
              <li>
                  <strong>Legitimate Interests: </strong>
                  We may process your data when it is reasonably necessary to achieve our legitimate business interests.
              </li>
              <br>
              <li>
                  <strong>Performance of a Contract: </strong>
                  Where we have entered into a contract with you, we may process your personal information to fulfill the terms of our contract.
              </li>
              <br>
              <li>
                  <strong>Legal Obligations: </strong>
                  We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process, such as in response to a court order or a subpoena (including in response to public authorities to meet national security or law enforcement requirements).
              </li>
              <br>
              <li>
                  <strong>Vital Interests:  </strong>
                  We may disclose your information where we believe it is necessary to investigate, prevent, or take action regarding potential violations of our policies, suspected fraud, situations involving potential threats to the safety of any person and illegal activities, or as evidence in litigation in which we are involved.
              </li>
          </ul>
          <br>
          <p>More specifically, we may need to process your data or share your personal information in the following situations:</p>
          <br>
          <ul>
              <li>
                  <strong>Business Transfers. </strong>
                  We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
              </li>
              <br>
              <li>
                  <strong>Third-Party Advertisers.  </strong>
                  We may use third-party advertising companies to serve ads when you visit our website. These companies may use information about your visits to our Website(s) and other websites that are contained in web cookies and other tracking technologies in order to provide advertisements about goods and services of interest to you.
              </li>
          </ul>
          <br>
          <h3 id="4" class="text-175 font-semibold">4. Do we use cookies and other tracking technologies?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We may use cookies and other tracking technologies to collect and store your information.
          </p>
          <br>
          <p>We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy.</p>
          <br>
          <h3 id="5" class="text-175 font-semibold">5. How long do we keep your information?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We keep your information for as long as necessary to fulfill the purposes outlined in this privacy policy unless otherwise required by law.
          </p>
          <br>
          <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law (such as tax, accounting or other legal requirements).</p>
          <p>DanDocs keeps your personal information for longer than 2 years past the termination of the user's account. When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize it, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.</p>
          <br>
          <h3 id="6" class="text-175 font-semibold">6. How do we keep your information safe?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We aim to protect your personal information through a system of organizational and technical security measures.
          </p>
          <br>
          <p>We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, please also remember that we cannot guarantee that the internet itself is 100% secure. Although we will do our best to protect your personal information, transmission of personal information to and from our website is at your own risk. You should only access the services within a secure environment.</p>
          <br>
          <h3 id="7" class="text-175 font-semibold">7. Do we collect information from minors?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              We do not knowingly collect data from or market to children under 18 years of age.
          </p>
          <br>
          <p>We do not knowingly solicit data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent’s use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we have collected from children under age 18, please contact us at support@dandocs.com.</p>
          <br>
          <h3 id="8" class="text-175 font-semibold">8. What are your privacy rights?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              In some regions, such as the European Economic Area, you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time.
          </p>
          <br>
          <p>In some regions (like the European Economic Area), you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal information; and (iv) if applicable, to data portability. In certain circumstances, you may also have the right to object to the processing of your personal information. To make such a request, please use the contact detail provided below. We will consider and act upon any request in accordance with applicable data protection laws.</p>
          <br>
          <p>If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time. Please note however that this will not affect the lawfulness of the processing before its withdrawal.</p>
          <br>
          <p>If you are resident in the European Economic Area and you believe we are unlawfully processing your personal information, you also have the right to complain to your local data protection supervisory authority. You can find their contact details here: <a href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm" target="_blank">http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm.</a></p>
          <br>
          <p>If you have questions or comments about your privacy rights, you may email us at support@dandocs.com</p>
          <br>
          <h4 class="text-lg font-semibold">Account Information</h4>
          <br>
          <p>If you would at any time like to review or change the information in your account or terminate your account, you can email us at support@dandocs.com</p>
          <br>
          <p>Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, some information may be retained in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our Terms of Use and/or comply with legal requirements.</p>
          <br>
          <p>
              <u>Cookies and similar technologies: </u>
              Most Web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this could affect certain features or services of our website.
          </p>
          <br>
          <p>
              <u>Opting out of email marketing: </u>
              You can unsubscribe from our marketing email list at any time by clicking on the unsubscribe link in the emails that we send or by contacting us at support@dandocs.com. You will then be removed from the marketing email list – however, we will still need to send you service-related emails that are necessary for the administration and use of your account. To otherwise opt-out, you may contact us at support@dandocs.com.
          </p>
          <br>
          <h3 id="9" class="text-175 font-semibold">9. Data breach</h3>
          <br>
          <p>A privacy breach occurs when there is unauthorized access to or collection, use, disclosure or disposal of personal information. You will be notified about data breaches when DanDocs believes you are likely to be at risk or serious harm. For example, a data breach may be likely to result in serious financial harm or harm to your mental or physical well-being. In the event that DanDocs becomes aware of a security breach which has resulted or may result in unauthorized access, use or disclosure of personal information DanDocs will promptly investigate the matter and notify the applicable Supervisory Authority not later than 72 hours after having become aware of it, unless the personal data breach is unlikely to result in a risk to the rights and freedoms of natural persons.</p>
          <br>
          <h3 id="10" class="text-175 font-semibold">10. Controls for do-not-track features</h3>
          <br>
          <p>Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track (“DNT”) feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. No uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this privacy policy.</p>
          <br>
          <h3 id="11" class="text-175 font-semibold">11. Do California residents have specific rights?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              Yes, if you are a resident of California, you are granted specific rights regarding access to your personal information.
          </p>
          <br>
          <p>California Civil Code Section 1798.83, also known as the “Shine The Light” law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us using the contact information provided below.</p>
          <br>
          <p>If you are under 18 years of age, reside in California, and have a registered account with the Services, you have the right to request removal of unwanted data that you publicly post on the Services. To request removal of such data, please contact us using the contact information provided below, and include the email address associated with your account and a statement that you reside in California. We will make sure the data is not publicly displayed on the Services, but please be aware that the data may not be completely or comprehensively removed from our systems.</p>
          <br>
          <h3 id="12" class="text-175 font-semibold">12. Do we make updates to this policy?</h3>
          <br>
          <p class="italic">
              <strong>In short: </strong>
              Yes, we will update this policy as necessary to stay compliant with relevant laws.
          </p>
          <br>
          <p>We may update this privacy policy from time to time. The updated version will be indicated by an updated “Revised” date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy policy, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.</p>
          <br>
          <h3 id="13" class="text-175 font-semibold">13. How can you contact us about this policy?</h3>
          <br>
          <p>If you have questions or comments about this policy, you may email us at support@dandocs.com or by post to:</p>
          <br>
          <p>DanDocs</p>
          <p>Symfonivej 18</p>
          <p>Herlev 2730</p>
          <p>Denmark</p>
          <br>
          <br>
          <h4 class="text-lg font-semibold">How can you review, update or delete the data we collect from you?</h4>
          <br>
          <p cass="font-semibold">Based on the laws of some countries, you may have the right to request access to the personal information we collect from you, change that information, or delete it in some circumstances. To request to review, update, or delete your personal information, please contact us via email at support@dandocs.com. We will respond to your request within 30 days.</p>
    </div>
  </div>
</template>

<script>
    export default {
        name: "PrivacyPolicy",
        methods: {
            handleClick(id) {
                let anchor = document.getElementById(id);
                if(anchor) {
                    anchor.scrollIntoView();
                }
            }
        }
    }
</script>

<style lang="scss" scoped>

  .privacy-policy {
      ol {
          padding-left: 10px;

          li {
              list-style: decimal;

              &:hover {
                  cursor: pointer;
                  text-decoration: underline;
              }
          }
      }

      ul {
          padding-left: 15px;
          margin-left: 1em;
          li {
              list-style: disc;
          }
      }
  }

</style>
